import { API_BASE } from '../config/api';

function getAuthHeaders(): Record<string, string> {
    const token = sessionStorage.getItem('clinicos_token');
    if (token && token !== 'demo-token') {
        return { Authorization: `Bearer ${token}` };
    }
    return {};
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
            ...options?.headers,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || `API Error: ${res.status}`);
    }

    return res.json();
}

// ── Types ───────────────────────────────────────────────────────────────

export interface Patient {
    id: string;
    fhirId: string;
    clinicPatientId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender?: string;
    mobilePhone: string;
    whatsappNumber?: string;
    email?: string;
    cedula?: string;
    isActive: boolean;
    createdAt: string;
}

export interface Appointment {
    id: string;
    patientId: string;
    doctorId: string;
    serviceType: string;
    appointmentType: 'in_person' | 'telehealth';
    status: string;
    scheduledStart: string;
    scheduledEnd: string;
    durationMinutes: number;
    chiefComplaint?: string;
    patient: { id: string; firstName: string; lastName: string; mobilePhone: string };
    doctor: { id: string; firstName: string; lastName: string; specialty?: string };
    location?: { id: string; name: string };
}

export interface SoapNote {
    id: string;
    appointmentId: string;
    patientId: string;
    doctorId: string;
    status: string;
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
    chiefComplaint?: string;
    vitalSigns?: Record<string, number>;
    diagnoses?: Array<{ icd10Code: string; description: string; type: string }>;
    signedAt?: string;
    createdAt: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
}

// ── API Functions ───────────────────────────────────────────────────────

export const api = {
    // Patients
    patients: {
        list: (params?: string) =>
            request<PaginatedResponse<Patient>>(`/patients${params ? `?${params}` : ''}`),
        get: (id: string) =>
            request<{ data: Patient & { appointments: Appointment[]; soapNotes: SoapNote[] } }>(`/patients/${id}`),
        create: (data: Partial<Patient>) =>
            request<{ data: Patient }>('/patients', { method: 'POST', body: JSON.stringify(data) }),
        update: (id: string, data: Partial<Patient>) =>
            request<{ data: Patient }>(`/patients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    },

    // Appointments
    appointments: {
        list: (params?: string) =>
            request<PaginatedResponse<Appointment>>(`/appointments${params ? `?${params}` : ''}`),
        get: (id: string) =>
            request<{ data: Appointment }>(`/appointments/${id}`),
        create: (data: Partial<Appointment>) =>
            request<{ data: Appointment }>('/appointments', { method: 'POST', body: JSON.stringify(data) }),
        updateStatus: (id: string, status: string, reason?: string) =>
            request<{ data: Appointment }>(`/appointments/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status, reason }),
            }),
    },

    // Encounters
    encounters: {
        get: (id: string) =>
            request<{ data: SoapNote }>(`/encounters/${id}`),
        create: (data: { appointmentId: string; chiefComplaint?: string; vitalSigns?: Record<string, number> }) =>
            request<{ data: SoapNote }>('/encounters', { method: 'POST', body: JSON.stringify(data) }),
        update: (id: string, data: Partial<SoapNote>) =>
            request<{ data: SoapNote }>(`/encounters/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
        sign: (id: string) =>
            request<{ data: SoapNote }>(`/encounters/${id}/sign`, { method: 'POST' }),
    },

    // Health
    health: () => request<{ status: string; services: Record<string, string> }>('/health'.replace('/api', '')),
};
